import { NetworkError } from '@/errors';
import type { IPayment } from '@/models/payments';
import { getOrderByIdWithPlans, getOrderByIdWithPlansAndPayments } from '@/services/orders';
import { useGeneralStore } from '@/stores/general';
import { useUserStore } from '@/stores/users';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import type { RouteLocationNormalizedGeneric } from 'vue-router';
import { CUSTOMER_UI_URL } from '../config';
import type { IPaymentPlan } from '../models/paymentPlans';
import { useMerchantStore } from '../stores/merchants';
import { useWidgetStore } from '../stores/widget';

export const getRedirectUrl = (orderId: string, payment: IPayment) => {
  const generalStore = useGeneralStore();
  let redirectUrl: URL;
  if (generalStore.notifyUrl) {
    const notifyUrl = generalStore.notifyUrl;
    redirectUrl = new URL(notifyUrl);

    redirectUrl.searchParams.append('orderId', orderId as string);
    redirectUrl.searchParams.append('success', payment.paid ? 'true' : 'false');
    redirectUrl.searchParams.append('paymentId', payment._id as string);
    redirectUrl.searchParams.append('pending', payment.pending ? 'true' : 'false');
  } else {
    redirectUrl = new URL(`${CUSTOMER_UI_URL}/dashboard/purchases/${orderId}`);
  }
  return redirectUrl.href;
};

export const initializeWidgetOrderGuard = async (to: RouteLocationNormalizedGeneric) => {
  const widgetStore = useWidgetStore();
  const userStore = useUserStore();

  const merchantStore = useMerchantStore();
  const id = to.params.id as string;
  try {
    // Merchants can't go through the widget flow.
    if (userStore.user && userStore.user.role === 'MERCHANT') {
      userStore.signOut();
      merchantStore.activeMerchantId = '';
    }

    if (!widgetStore.boost?.partialOrder || widgetStore.boost.partialOrder.type !== 'REWARDS') {
      widgetStore.order =
        to.name === 'Contribute'
          ? await getOrderByIdWithPlansAndPayments(id as string)
          : await getOrderByIdWithPlans(id as string);
      addMetric('orders-getOrderByIdWithPlans', { status: 'success', channel: 'merchant' });
      widgetStore.plan = widgetStore.getPaymentPlan() as IPaymentPlan;
      if (
        widgetStore.order.state === 'COMPLETED' ||
        widgetStore.order.state === 'CANCELLED' ||
        widgetStore.order.state === 'EXPIRED'
      ) {
        window.location.href = `${CUSTOMER_UI_URL}/dashboard/purchases/${id}`;
        return;
      }
      const isPaymentLink =
        to.query.paymentMethod || to.query.amount || to.name === 'Contribute' || to.name === 'DebiCheck';

      // Used when returning from the payment breakdown on the contribution screen.
      if (to.name === 'Contribute' && widgetStore.order.type === 'REWARDS') {
        return;
      }
      if (
        (widgetStore.order.state !== 'PARTIAL' ||
          (widgetStore.order.type === 'REWARDS' && userStore.user.verifiedEmail)) &&
        !isPaymentLink
      ) {
        return {
          name: 'Contribute',
          params: { id },
        };
      } else if (widgetStore.order.state === 'PARTIAL' && to.name === 'Contribute') {
        return {
          name: 'WidgetSignIn',
          params: {
            id,
          },
        };
      }
    }
    return;
  } catch (error: any) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('orders-getOrderByIdWithPlans', { status: 'network_error', channel: 'merchant' });
    } else {
      createException(error);
      addMetric('orders-getOrderByIdWithPlans', { status: 'error', channel: 'merchant' });
    }

    if (userStore.isAuthenticated) {
      window.location.href = `${CUSTOMER_UI_URL}/dashboard`;
      return;
    }
    return {
      name: 'shopperSignIn',
      query: { redirect: to.fullPath },
    };
  }
};

export const fixStateRedirectGuard = (to: RouteLocationNormalizedGeneric) => {
  const widgetStore = useWidgetStore();

  const id = to.params.id as string;
  if (!widgetStore.id) {
    return {
      name: widgetStore.order.state === 'PARTIAL' ? 'WidgetSignIn' : 'Contribute',
      params: { id },
    };
  }
  return;
};
